import React, { useCallback, useEffect } from 'react';
import * as Haptics from 'expo-haptics';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { useSharedValue, withDelay, withTiming } from 'react-native-reanimated';

import { Button, theme, VStack } from '@sg/garnish';
import { PermissionsPrompt } from '@sg/shared-fe/features/permissions';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationPermissionPrimer = (props: Props) => {
  const { isRequesting, isSkipping, onAllow, onSkip } = props;

  const { formatMessage } = useIntl();

  // ─── Animations ──────────────────────────────────────────────────────

  const hexagonScaleSV = useSharedValue(HEXAGON_INITIAL_SCALE);
  const hexagonOverlayOpacitySV = useSharedValue(0);

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleAllow = useCallback(() => {
    void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onAllow();
  }, [onAllow]);

  const handleSkip = useCallback(() => {
    void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onSkip();
  }, [onSkip]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    hexagonScaleSV.value = withDelay(
      HEXAGON_SCALE_START_DELAY,
      withTiming(HEXAGON_TARGET_SCALE, { duration: HEXAGON_SCALE_DURATION }),
    );
    hexagonOverlayOpacitySV.value = withDelay(HEXAGON_OVERLAY_START_DELAY, withTiming(1));
  }, [hexagonScaleSV, hexagonOverlayOpacitySV]);

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <PermissionsPrompt.Container>
      <View style={styles.hexagonsContainer}>
        <PermissionsPrompt.Hexagon scaleSV={hexagonScaleSV} opacitySV={hexagonOverlayOpacitySV} />

        {HEXAGON_STROKES.map((stroke, index) => (
          <PermissionsPrompt.HexagonStroke key={index} scale={stroke.scale} delay={stroke.delay} />
        ))}
      </View>

      <PermissionsPrompt.ContentContainer enteringDelay={CONTENT_ENTERING_DELAY}>
        <PermissionsPrompt.Title>
          <FormattedMessage {...messages.title} values={{ br: renderBreak }} />
        </PermissionsPrompt.Title>

        <PermissionsPrompt.Text>
          <FormattedMessage {...messages.text} />
        </PermissionsPrompt.Text>

        <VStack gap={theme.spacing['2']}>
          <Button
            onPress={handleSkip}
            palette="primary"
            size="large"
            isLoading={isSkipping}
            disabled={isRequesting}
          >
            {formatMessage(messages.notNow)}
          </Button>

          <Button
            onPress={handleAllow}
            palette="oatmeal"
            size="large"
            disabled={isSkipping}
            isLoading={isRequesting}
          >
            {formatMessage(messages.enableLocation)}
          </Button>
        </VStack>
      </PermissionsPrompt.ContentContainer>
    </PermissionsPrompt.Container>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function renderBreak() {
  return '\n';
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  hexagonsContainer: {
    position: 'absolute',
    top: '10%',
    left: 0,
    right: 0,
    bottom: '30%',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const HEXAGON_SCALE_START_DELAY = 500;
const HEXAGON_SCALE_DURATION = 1000;
const HEXAGON_INITIAL_SCALE = 1.4;
const HEXAGON_TARGET_SCALE = 0.06;

const HEXAGON_OVERLAY_START_DELAY = HEXAGON_SCALE_START_DELAY + HEXAGON_SCALE_DURATION;

const HEXAGON_STROKES_COUNT = 7;
const HEXAGON_STROKE_MIN_SCALE = 0.18;
const HEXAGON_STROKE_MAX_SCALE = HEXAGON_INITIAL_SCALE;
const HEXAGON_STROKE_START_DELAY = HEXAGON_OVERLAY_START_DELAY;
const HEXAGON_STROKE_DELAY_STEP = 150;

const HEXAGON_STROKE_SCALE_RATIO =
  (HEXAGON_STROKE_MAX_SCALE / HEXAGON_STROKE_MIN_SCALE) ** (1 / (HEXAGON_STROKES_COUNT - 1));

const HEXAGON_STROKES = Array.from({ length: HEXAGON_STROKES_COUNT }, (_, index) => ({
  scale: HEXAGON_STROKE_MIN_SCALE * HEXAGON_STROKE_SCALE_RATIO ** index,
  delay: HEXAGON_STROKE_START_DELAY + index * HEXAGON_STROKE_DELAY_STEP,
}));

// Content fades in after the last stroke starts + one more base delay.
const CONTENT_ENTERING_DELAY =
  HEXAGON_STROKE_START_DELAY +
  (HEXAGON_STROKES_COUNT - 1) * HEXAGON_STROKE_DELAY_STEP +
  HEXAGON_SCALE_START_DELAY;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Enable<br></br>location for<br></br>faster ordering.',
    description: 'Location Permissions | Title',
  },
  text: {
    defaultMessage: 'Enable location settings for personalized offers and deals.',
    description: 'Location Permissions | Body text',
  },
  notNow: {
    defaultMessage: 'Not now',
    description: 'Location Permissions | Decline CTA label',
  },
  enableLocation: {
    defaultMessage: 'Enable Location Services',
    description: 'Location Permissions | Accept CTA label',
  },
  debugClearSuppression: {
    defaultMessage: '[debug] Clear suppression',
    description: 'Location Permissions | Debug CTA to clear stored suppression',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isRequesting: boolean;
  isSkipping: boolean;
  onAllow: () => void;
  onSkip: () => void;
};
